/**
 * Years of experience for EMT registration / profile.
 *
 * **Current UI:** exact integers 0–19 plus "20+" (select value `"20"`).
 * **Canonical Firestore:** `professional.experience.years` — number **0–20** inclusive,
 * where **20 means twenty or more years** (the former "20+" choice). No separate string needed.
 *
 * **Legacy:** `professional.experience.yearsRange` bands (`0-1`, `1-3`, …) — still read for
 * readiness fallback and mapped to honest lower-bound select values when loading the form.
 */
import type { EMTProfile } from "../types/firestore";
import { getApproxExperienceYears, getSafePastExperience } from "./pastExperience";

/** Stored years value for the "20+" menu option (twenty or more years). */
export const EXPERIENCE_YEARS_CAP = 20;

/** Legacy registration bands (before exact-year picker). */
export const LEGACY_EXPERIENCE_RANGE_KEYS = ["0-1", "1-3", "3-5", "5-10", "10+"] as const;
export type LegacyExperienceYearsRange = (typeof LEGACY_EXPERIENCE_RANGE_KEYS)[number];

export function isLegacyExperienceYearsRange(s: string): s is LegacyExperienceYearsRange {
  return (LEGACY_EXPERIENCE_RANGE_KEYS as readonly string[]).includes(s);
}

/** Select values: "0" … "19", and "20" meaning 20+ (stored as years === 20). */
export const EXPERIENCE_YEAR_SELECT_VALUES: readonly string[] = [
  ...Array.from({ length: 20 }, (_, i) => String(i)),
  "20",
] as const;

export function isValidExperienceYearsSelectValue(s: string): boolean {
  return (EXPERIENCE_YEAR_SELECT_VALUES as readonly string[]).includes(s);
}

/** Map UI select value to canonical stored years (0–20). */
export function selectValueToStoredYears(value: string): number {
  if (!isValidExperienceYearsSelectValue(value)) return 0;
  return value === "20" ? EXPERIENCE_YEARS_CAP : Number(value);
}

/** Dropdown label: "0 years", "1 year", "N years", "20+". */
export function formatExperienceYearsMenuLabel(value: string): string {
  if (value === "20") return "20+";
  if (value === "1") return "1 year";
  if (value === "0") return "0 years";
  return `${value} years`;
}

/**
 * Legacy bands → representative years for readiness when ONLY yearsRange exists (no numeric years).
 */
export function legacyRangeToRepresentativeYears(range: string): number {
  if (!range || !isLegacyExperienceYearsRange(range)) return 0;
  const map: Record<LegacyExperienceYearsRange, number> = {
    "0-1": 1,
    "1-3": 2,
    "3-5": 4,
    "5-10": 7,
    "10+": 12,
  };
  return map[range];
}

/**
 * When loading Step 2 with only legacy yearsRange, map to a select value (lower bound of band).
 * Avoids implying a misleading midpoint (e.g. showing "4" for "3–5").
 */
export function legacyRangeToSelectValue(range: LegacyExperienceYearsRange): string {
  const lower: Record<LegacyExperienceYearsRange, number> = {
    "0-1": 0,
    "1-3": 1,
    "3-5": 3,
    "5-10": 5,
    "10+": 10,
  };
  const n = lower[range];
  return String(Math.min(n, 19));
}

/**
 * Build controlled Select value from profile.experience (numeric years, legacy range, or empty).
 */
export function profileExperienceToSelectValue(
  exp: { years?: number; yearsRange?: string } | undefined
): string {
  if (!exp) return "";

  if (typeof exp.years === "number" && Number.isFinite(exp.years)) {
    const y = Math.max(0, exp.years);
    if (y >= EXPERIENCE_YEARS_CAP) return "20";
    const floored = Math.floor(y);
    if (floored >= 0 && floored <= 19) return String(floored);
    return "";
  }

  const r = exp.yearsRange;
  if (typeof r === "string" && isLegacyExperienceYearsRange(r)) {
    return legacyRangeToSelectValue(r);
  }

  return "";
}

/**
 * Single source for readiness / FieldReady "years experience" input.
 */
export function getYearsExperienceForReadiness(profile: EMTProfile | null | undefined, todayYmd?: string): number {
  if (!profile) return 0;

  const pastExpEntries = getSafePastExperience(profile);
  const derivedYears = getApproxExperienceYears(pastExpEntries, todayYmd);
  if (derivedYears > 0) return derivedYears;

  const exp = profile.professional?.experience;
  const rawYears = exp?.years;

  if (typeof rawYears === "number" && Number.isFinite(rawYears)) {
    return Math.max(0, rawYears);
  }

  const range =
    exp && typeof (exp as { yearsRange?: string }).yearsRange === "string"
      ? (exp as { yearsRange?: string }).yearsRange
      : undefined;
  if (range && isLegacyExperienceYearsRange(range)) {
    return legacyRangeToRepresentativeYears(range);
  }

  return 0;
}
